import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MultilineInput } from "@/components/ui/multiline-input";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@somethings-ui/web";
import { useForm } from "react-hook-form";
import { z } from "zod";

const messageSchema = z.object({
  message: z.string().trim().min(1, "Enter a message"),
});

type MessageForm = z.infer<typeof messageSchema>;

interface MessageComposerDialogProps {
  open: boolean;
  title: string;
  description?: string;
  placeholder: string;
  submitLabel?: string;
  isPending?: boolean;
  contentClassName?: string;
  titleClassName?: string;
  inputClassName?: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (message: string) => Promise<void>;
}

export function MessageComposerDialog({
  open,
  title,
  description,
  placeholder,
  submitLabel = "Send",
  isPending = false,
  contentClassName,
  titleClassName,
  inputClassName,
  onOpenChange,
  onSubmit,
}: MessageComposerDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MessageForm>({
    resolver: zodResolver(messageSchema),
    defaultValues: { message: "" },
  });
  const isEmpty = !watch("message").trim();

  const submit = handleSubmit(async ({ message }) => {
    try {
      await onSubmit(message.trim());
      reset();
      onOpenChange(false);
    } catch {
      return;
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) reset();
      }}
    >
      <DialogContent className={cn("sm:max-w-2xl", contentClassName)}>
        <DialogHeader>
          <DialogTitle
            className={cn("text-header-4 text-text-primary", titleClassName)}
          >
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-body-2 text-text-secondary">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <form className="space-y-5" onSubmit={submit}>
          <div>
            <MultilineInput
              {...register("message")}
              aria-label={title}
              placeholder={placeholder}
              className={cn("min-h-28", inputClassName)}
            />
            {errors.message && (
              <p className="mt-1 text-body-2 text-states-error">
                {errors.message.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="submit"
              variant="primary"
              fullWidth={false}
              loading={isPending || isSubmitting}
              disabled={isEmpty || isPending || isSubmitting}
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
